/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import toast from "react-hot-toast";
import { Loader, Header } from "../../components";
import { useAuthContext } from "../../context/auth";
import {
  getEmpleados,
  crearEmpleado,
  actualizarEmpleado,
  getVacacionesEmpleado,
  agregarVacaciones,
  getRemuneracionesEmpleado,
  crearRemuneracion,
  actualizarRemuneracion,
} from "../../utils";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./stylesEmpleados.module.scss";

const GestionEmpleados = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showVacaciones, setShowVacaciones] = useState(false);
  const [showRemuneraciones, setShowRemuneraciones] = useState(false);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [vacaciones, setVacaciones] = useState([]);
  const [remuneraciones, setRemuneraciones] = useState([]);
  const [remuneracionEditando, setRemuneracionEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    fecha_nacimiento: "",
    inicio_contrato: "",
    fin_contrato: "",
    sueldo_base: "",
    comision_porcentaje: "",
    pais,
  });
  const [vacacionForm, setVacacionForm] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    notas: "",
  });
  const [showEgreso, setShowEgreso] = useState(false);
  const [egresoForm, setEgresoForm] = useState({
    egreso_fecha: "",
    egreso_monto: "",
  });
  const [remuneracionForm, setRemuneracionForm] = useState({
    inicio_vigencia: "",
    fin_vigencia: "",
    sueldo_base: "",
    comision_porcentaje: "",
    bonos_fijo: "",
    bonos_variable: "",
    notas: "",
  });

  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        setLoading(true);
        const data = await getEmpleados(pais);
        setEmpleados(data || []);
      } catch (error) {
        console.error("Error al cargar empleados:", error);
        alert("Error al cargar empleados");
      } finally {
        setLoading(false);
      }
    };
    fetchEmpleados();
  }, [pais]);

  useEffect(() => {
    if (empleados.length > 0) {
      const fetchAllRemuneraciones = async () => {
        try {
          const allRem = [];
          for (const emp of empleados) {
            const data = await getRemuneracionesEmpleado(emp.id);
            allRem.push(...(data || []));
          }
          setRemuneraciones(allRem);
        } catch (error) {
          console.error("Error al cargar todas las remuneraciones:", error);
        }
      };
      fetchAllRemuneraciones();
    }
  }, [empleados]);

  const calcularVacaciones = (empleado) => {
    if (!empleado.inicio_contrato) return { generados: 0, usados: 0, disponibles: 0 };
    const inicio = new Date(empleado.inicio_contrato);
    const hoy = new Date();
    const anosTrabajados = (hoy - inicio) / (1000 * 60 * 60 * 24 * 365.25);
    const diasGenerados = Math.floor(anosTrabajados * 15);
    const diasUsados = vacaciones.reduce((total, v) => {
      if (v.empleado_id === empleado.id) {
        const inicioV = new Date(v.fecha_inicio);
        const finV = new Date(v.fecha_fin);
        const dias = Math.ceil((finV - inicioV) / (1000 * 60 * 60 * 24)) + 1;
        return total + dias;
      }
      return total;
    }, 0);
    return {
      generados: diasGenerados,
      usados: diasUsados,
      disponibles: Math.max(0, diasGenerados - diasUsados),
    };
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    return new Date(fechaStr + "T12:00:00").toLocaleDateString("es", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleOpenForm = (empleado = null) => {
    if (empleado) {
      setFormData({
        ...empleado,
        fecha_nacimiento: empleado.fecha_nacimiento || "",
        fin_contrato: empleado.fin_contrato || "",
      });
      setEmpleadoSeleccionado(empleado);
    } else {
      setFormData({
        nombre: "",
        apellido: "",
        fecha_nacimiento: "",
        inicio_contrato: "",
        fin_contrato: "",
        sueldo_base: "",
        comision_porcentaje: "",
        pais,
      });
      setEmpleadoSeleccionado(null);
    }
    setShowForm(true);
  };

  const handleSaveEmpleado = async () => {
    try {
      const cleanedData = {
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        fecha_nacimiento: formData.fecha_nacimiento || null,
        inicio_contrato: formData.inicio_contrato,
        fin_contrato: formData.fin_contrato || null,
        sueldo_base: parseFloat(formData.sueldo_base) || 0,
        comision_porcentaje: parseFloat(formData.comision_porcentaje) || 0,
        pais,
      };

      await (empleadoSeleccionado
        ? actualizarEmpleado(empleadoSeleccionado.id, cleanedData)
        : crearEmpleado(cleanedData));
      setShowForm(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
    } catch (error) {
      console.error("Error al guardar empleado:", error);
      alert("Error al guardar empleado");
    }
  };

  const handleOpenVacaciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    try {
      const data = await getVacacionesEmpleado(empleado.id);
      setVacaciones(data || []);
    } catch {
      setVacaciones([]);
    }
    setShowVacaciones(true);
  };

  const handleAddVacacion = async () => {
    try {
      await agregarVacaciones({
        empleado_id: empleadoSeleccionado.id,
        ...vacacionForm,
      });
      setVacacionForm({ fecha_inicio: "", fecha_fin: "", notas: "" });
      const data = await getVacacionesEmpleado(empleadoSeleccionado.id);
      setVacaciones(data || []);
    } catch {
      alert("Error al agregar vacaciones");
    }
  };

  const handleOpenRemuneraciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setRemuneracionEditando(null);
    setRemuneracionForm({
      inicio_vigencia: "",
      fin_vigencia: "",
      sueldo_base: "",
      comision_porcentaje: "",
      bonos_fijo: "",
      bonos_variable: "",
      notas: "",
    });
    try {
      const data = await getRemuneracionesEmpleado(empleado.id);
      setRemuneraciones(data || []);
    } catch {
      setRemuneraciones([]);
    }
    setShowRemuneraciones(true);
  };

  const handleOpenEgreso = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setEgresoForm({
      egreso_fecha: empleado.egreso_fecha || "",
      egreso_monto: empleado.egreso_monto || "",
    });
    setShowEgreso(true);
  };

  const handleSaveEgreso = async () => {
    try {
      await actualizarEmpleado(empleadoSeleccionado.id, {
        egreso_fecha: egresoForm.egreso_fecha || null,
        egreso_monto: parseFloat(egresoForm.egreso_monto) || null,
      });
      setShowEgreso(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
      toast.success("Egreso guardado correctamente");
    } catch (error) {
      console.error("Error al guardar egreso:", error);
      toast.error("Error al guardar egreso");
    }
  };

  const handleEditRemuneracion = (rem) => {
    setRemuneracionEditando(rem);
    setRemuneracionForm({
      inicio_vigencia: rem.inicio_vigencia,
      fin_vigencia: rem.fin_vigencia || "",
      sueldo_base: rem.sueldo_base,
      comision_porcentaje: rem.comision_porcentaje,
      bonos_fijo: rem.bonos_fijo,
      bonos_variable: rem.bonos_variable,
      notas: rem.notas || "",
    });
  };

  const handleSaveRemuneracion = async () => {
    try {
      const cleanedData = {
        inicio_vigencia: remuneracionForm.inicio_vigencia,
        fin_vigencia: remuneracionForm.fin_vigencia || null,
        sueldo_base: parseFloat(remuneracionForm.sueldo_base) || 0,
        comision_porcentaje: parseFloat(remuneracionForm.comision_porcentaje) || 0,
        bonos_fijo: parseFloat(remuneracionForm.bonos_fijo) || 0,
        bonos_variable: parseFloat(remuneracionForm.bonos_variable) || 0,
        notas: remuneracionForm.notas.trim() || null,
      };

      let newRemuneraciones;
      if (remuneracionEditando) {
        const result = await actualizarRemuneracion(remuneracionEditando.id, cleanedData);
        newRemuneraciones = remuneraciones.map((r) => (r.id === remuneracionEditando.id ? result[0] : r));
      } else {
        const result = await crearRemuneracion({
          empleado_id: empleadoSeleccionado.id,
          ...cleanedData,
        });
        newRemuneraciones = [...remuneraciones, result[0]];
      }

      // Actualiza el state local del modal
      setRemuneraciones(newRemuneraciones);
      setRemuneracionEditando(null);
      setRemuneracionForm({
        inicio_vigencia: "",
        fin_vigencia: "",
        sueldo_base: "",
        comision_porcentaje: "",
        bonos_fijo: "",
        bonos_variable: "",
        notas: "",
      });

      // Recarga empleados para que la tabla principal recalcule subrows
      const data = await getEmpleados(pais);
      setEmpleados(data || []);

      // Recarga remuneraciones del empleado actual para mantener sincronizado el modal
      const updatedRem = await getRemuneracionesEmpleado(empleadoSeleccionado.id);
      setRemuneraciones(updatedRem || []);
    } catch (error) {
      console.error("Error al guardar remuneración:", error);
      alert("Error al guardar remuneración");
    }
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.container}>
      <Header>
        <Button variant="primary" onClick={() => handleOpenForm()} className={styles.nuevoBtn}>
          Nuevo Empleado
        </Button>
      </Header>

      <div className={styles.empleadosTableWrapper}>
        <Table striped bordered hover responsive className={styles.empleadosTable}>
          <thead>
            <tr>
              <th rowSpan={2} className={styles.colNombreCompleto}>
                Nombre
              </th>
              <th colSpan={2} className={styles.colFechas}>
                Contrato
              </th>
              <th colSpan={3} className={styles.colSueldoGroup}>
                Sueldo
              </th>
              <th colSpan={3} className={styles.colVacacionesGroup}>
                Vacaciones
              </th>
              <th rowSpan={2} className={styles.colAcciones}>
                Editar
              </th>
            </tr>
            <tr>
              <th>Inicio</th> {/* Contrato inicio */}
              <th>Fin</th> {/* Contrato fin */}
              <th>Inicio</th> {/* Vigencia remuneración */}
              <th>Monto</th>
              <th>Comisión</th> {/* Comisión % */}
              <th>Generados</th>
              <th>Usadas</th>
              <th>Disponibles</th>
            </tr>
          </thead>
          <tbody>
            {empleados.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center">
                  No hay empleados registrados
                </td>
              </tr>
            ) : (
              [...empleados]
                .sort((a, b) => {
                  // 1. Empleados activos (sin fin_contrato o fin_contrato futuro) arriba
                  const activoA = !a.fin_contrato || new Date(a.fin_contrato) > new Date();
                  const activoB = !b.fin_contrato || new Date(b.fin_contrato) > new Date();
                  if (activoA && !activoB) return -1; // a arriba
                  if (!activoA && activoB) return 1; // b arriba

                  // 2. Si ambos activos o ambos inactivos, ordenar por antigüedad (más antiguo primero)
                  const fechaA = new Date(a.inicio_contrato);
                  const fechaB = new Date(b.inicio_contrato);
                  if (fechaA.getTime() !== fechaB.getTime()) {
                    return fechaA - fechaB; // ascendente (antiguo primero)
                  }

                  // 3. Si misma antigüedad, por sueldo base descendente
                  const sueldoA = parseFloat(a.sueldo_base) || 0;
                  const sueldoB = parseFloat(b.sueldo_base) || 0;
                  return sueldoB - sueldoA;
                })
                .map((emp) => {
                  const vac = calcularVacaciones(emp);
                  const empRemuneraciones = remuneraciones.filter((r) => r.empleado_id === emp.id);
                  const remuneracionesCount = empRemuneraciones.length || 1;
                  return (
                    <React.Fragment key={emp.id}>
                      <tr>
                        <td rowSpan={remuneracionesCount} className={styles.colNombreCompleto}>
                          {`${emp.nombre} ${emp.apellido}`}
                        </td>
                        <td rowSpan={remuneracionesCount}>{formatFecha(emp.inicio_contrato)}</td>
                        <td rowSpan={remuneracionesCount}>{formatFecha(emp.fin_contrato)}</td>
                        {empRemuneraciones.length > 0 ? (
                          <>
                            <td>{formatFecha(empRemuneraciones[0].inicio_vigencia)}</td>
                            <td style={{ textAlign: "right" }}>
                              {formatAmount(empRemuneraciones[0].sueldo_base, pais === "Chile" ? "CLP" : "PEN")}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>-</td>
                            <td style={{ textAlign: "right" }}>
                              {formatAmount(emp.sueldo_base, pais === "Chile" ? "CLP" : "PEN")}
                            </td>
                          </>
                        )}
                        <td rowSpan={remuneracionesCount}>{emp.comision_porcentaje || 0}%</td>
                        <td rowSpan={remuneracionesCount}>{vac.generados}</td>
                        <td rowSpan={remuneracionesCount}>{vac.usados}</td>
                        <td rowSpan={remuneracionesCount}>{vac.disponibles}</td>
                        <td rowSpan={remuneracionesCount}>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              onClick={() => handleOpenForm(emp)}
                              disabled={!!emp.fin_contrato}
                              style={{
                                minWidth: "70px",
                                backgroundColor: "#2563eb",
                                borderColor: "#2563eb",
                                color: "white",
                              }}
                            >
                              Datos
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleOpenVacaciones(emp)}
                              disabled={!!emp.fin_contrato}
                              style={{
                                minWidth: "70px",
                                backgroundColor: "#0891b2",
                                borderColor: "#0891b2",
                                color: "white",
                              }}
                            >
                              Vacas
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleOpenRemuneraciones(emp)}
                              disabled={!!emp.fin_contrato}
                              style={{
                                minWidth: "70px",
                                backgroundColor: "#ca8a04",
                                borderColor: "#ca8a04",
                                color: "white",
                              }}
                            >
                              Sueldo
                            </Button>
                            {emp.fin_contrato && (
                              <Button
                                size="sm"
                                onClick={() => handleOpenEgreso(emp)}
                                style={{
                                  minWidth: "70px",
                                  backgroundColor: "#dc2626",
                                  borderColor: "#dc2626",
                                  color: "white",
                                }}
                              >
                                Egreso
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {empRemuneraciones.slice(1).map((rem) => (
                        <tr key={rem.id}>
                          <td>{formatFecha(rem.inicio_vigencia)}</td>
                          <td style={{ textAlign: "right" }}>
                            {formatAmount(rem.sueldo_base, pais === "Chile" ? "CLP" : "PEN")}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal Formulario Empleado */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{empleadoSeleccionado ? "Editar" : "Nuevo"} Empleado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fecha Nacimiento</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Inicio Contrato</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={formData.inicio_contrato}
                  onChange={(e) => setFormData({ ...formData, inicio_contrato: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fin Contrato (opcional)</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fin_contrato}
                  onChange={(e) => setFormData({ ...formData, fin_contrato: e.target.value || null })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Sueldo Base</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.sueldo_base}
                  onChange={(e) => setFormData({ ...formData, sueldo_base: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Comisión %</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.comision_porcentaje}
                  onChange={(e) => setFormData({ ...formData, comision_porcentaje: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEmpleado}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Vacaciones */}
      <Modal show={showVacaciones} onHide={() => setShowVacaciones(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Vacaciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h5>Agregar Período</h5>
            <div className="row">
              <Form.Group className="col-md-4">
                <Form.Label>Inicio</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_inicio}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_inicio: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Fin</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_fin}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_fin: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  value={vacacionForm.notas}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, notas: e.target.value })}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end">
                <Button variant="success" onClick={handleAddVacacion}>
                  +
                </Button>
              </div>
            </div>
          </div>

          <h5>Historial</h5>
          <Table striped>
            <thead>
              <tr>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Días</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {vacaciones.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    Sin registros
                  </td>
                </tr>
              ) : (
                vacaciones.map((v) => {
                  const dias =
                    Math.ceil((new Date(v.fecha_fin) - new Date(v.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <tr key={v.id}>
                      <td>{formatFecha(v.fecha_inicio)}</td>
                      <td>{formatFecha(v.fecha_fin)}</td>
                      <td>{dias}</td>
                      <td>{v.notas || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {empleadoSeleccionado && (
            <div className="mt-3 text-end">
              <strong>Disponibles: {calcularVacaciones(empleadoSeleccionado).disponibles} días</strong>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVacaciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Remuneraciones */}
      <Modal show={showRemuneraciones} onHide={() => setShowRemuneraciones(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Remuneraciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 p-3 border rounded">
            <h5>{remuneracionEditando ? "Editar" : "Agregar"} Remuneración</h5>
            <div className="row">
              <Form.Group className="col-md-3 mb-3">
                <Form.Label>Inicio Vigencia</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={remuneracionForm.inicio_vigencia}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, inicio_vigencia: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Sueldo Base</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.sueldo_base}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, sueldo_base: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Comisión %</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.comision_porcentaje}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, comision_porcentaje: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Bonos Fijo</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.bonos_fijo}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, bonos_fijo: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Bonos Variable</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.bonos_variable}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, bonos_variable: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-3 mb-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  value={remuneracionForm.notas}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, notas: e.target.value })}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end mb-3">
                <Button variant="success" onClick={handleSaveRemuneracion}>
                  {remuneracionEditando ? "Actualizar" : "+"}
                </Button>
              </div>
            </div>
          </div>

          <h5>Historial de Remuneraciones</h5>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Inicio Vigencia</th>
                <th>Sueldo Base</th>
                <th>Comisión %</th>
                <th>Bonos Fijo</th>
                <th>Bonos Variable</th>
                <th>Notas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {remuneraciones.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    Sin registros de remuneraciones
                  </td>
                </tr>
              ) : (
                remuneraciones.map((rem) => (
                  <tr key={rem.id}>
                    <td>{formatFecha(rem.inicio_vigencia)}</td>
                    <td>{formatAmount(rem.sueldo_base, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{rem.comision_porcentaje || 0}%</td>
                    <td>{formatAmount(rem.bonos_fijo, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{formatAmount(rem.bonos_variable, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{rem.notas || "-"}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => handleEditRemuneracion(rem)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemuneraciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal Egreso */}
      <Modal show={showEgreso} onHide={() => setShowEgreso(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Egreso - {empleadoSeleccionado && `${empleadoSeleccionado.nombre} ${empleadoSeleccionado.apellido}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Pago</Form.Label>
              <Form.Control
                type="date"
                value={egresoForm.egreso_fecha}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_fecha: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto {pais === "Chile" ? "(CLP)" : "(PEN)"}</Form.Label>
              <Form.Control
                type="number"
                value={egresoForm.egreso_monto}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_monto: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEgreso(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEgreso}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionEmpleados;
